import { Search16 } from '@carbon/icons-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import styled from 'styled-components';
import { User } from 'api/models';
import { Button } from 'components/atoms/button';
import { Select, UserComponents } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
// import { CustomOption } from '../UserOption';

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
};

const DropdownIndicator = (props) => {
  return (
    <RsComponents.DropdownIndicator {...props}>
      <Search16 />
    </RsComponents.DropdownIndicator>
  );
};

export const SelectContainer = styled.div`
  min-height: 350px;
  width: 296px;
  display: flex;
  flex-direction: column;
  background: white;

  .ant-btn-tertiary {
    margin-bottom: var(--spacing-03);
    margin-left: var(--spacing-03);
    margin-right: var(--spacing-03);
    justify-content: center;
    padding: initial;
  }

  .ozone-select__control {
    margin-top: 0;
  }

  .ant-list-item-meta-description {
    color: var(--gray-70);
  }

  .ozone-select__option--is-selected .ant-list-item-meta-description {
    color: var(--white-100);
  }
`;

export const Title = styled.span`
  --popover-spacing: var(--spacing-05);
  margin: var(--popover-spacing) var(--popover-spacing)
    calc(var(--popover-spacing) / 2);
  font-size: 0.875rem;
  line-height: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.16px;
`;

export const userToOption = (user) => ({
  ...user,
  label: user?.lastName
    ? `${user.firstName} ${user?.lastName}`
    : `${user.firstName}`,
  value: user.userId,
  imageUrl: user.photoUrl,
  description: user?.role?.roleName,
});

interface Props {
  users: User[];
  selected: User[];
  isLoading: boolean;
  title: any;
  onComplete?: (props?) => void;
  isMulti?: boolean;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  stopPropogation?: boolean;
  style?: any;
}

export const PeopleEditor = (props: Props) => {
  const {
    users,
    selected,
    isLoading,
    title,
    onComplete,
    isMulti,
    closeMenuOnSelect,
    hideSelectedOptions,
    stopPropogation = true,
    style,
  } = props;
  const [selectedUsers, setSelectedUsers] = useState<User[]>(selected);
  const [searchedText, setSearchedText] = useState('');

  const onSelection = (selectedValues: any[]) => {
    const selectedUserIds = selectedValues.map((val) => val.userId);

    if (!isMulti) {
      // If User is deselected, no option is selected, set to empty array.
      if (selectedValues.length === 0) {
        setSelectedUsers([]);
      } else {
        setSelectedUsers(
          users.filter((user) => user.userId === selectedValues.at(-1).userId),
        );
      }

      return;
    }

    setSelectedUsers(
      users.filter((user) => selectedUserIds.includes(user.userId)),
    );
  };

  const commonProps = {
    isMulti: isMulti || true,
    menuIsOpen: true,
    autoFocus: true,
    components: {
      ...UserComponents,
      ClearIndicator: null,
      IndicatorSeparator: null,
      Menu: VirtualizedMenuList,
      DropdownIndicator,
    },
    isClearable: true,
    tabSelectsValue: false,
    styles: selectStyles,
    onChange: onSelection,
    searchedText,
    closeMenuOnSelect: closeMenuOnSelect || false,
    onInputChange: (input) => setSearchedText(input),
  };

  const options = [...users];

  return (
    <SelectContainer style={style ?? null}>
      <Title>{title}</Title>
      <Select
        {...commonProps}
        value={isLoading ? [] : selectedUsers.map(userToOption)}
        options={isLoading ? [] : options.map(userToOption)}
        controlShouldRenderValue={false}
        hideSelectedOptions={
          hideSelectedOptions === undefined ? false : hideSelectedOptions
        }
        type="user"
        placeholder={<FormattedMessage id="placeholder.search" />}
        isLoading={isLoading}
      />
      <Button
        type="tertiary"
        style={{ marginTop: 'auto' }}
        size="middle"
        onClick={(e) => {
          onComplete?.(selectedUsers);
          if (stopPropogation) {
            e.stopPropagation();
          }
        }}
      >
        <FormattedMessage id="done" />
      </Button>
    </SelectContainer>
  );
};
